// inspired by the gist, continuously updated / improved to accept sync and async, return tuples, etc
// https://gist.github.com/t3dotgg/a486c4ae66d32bf17c09c73609dacc5b?permalink_comment_id=5511823#gistcomment-5511823

// this method is an alternative to the try catch block
// traditional try catch blocks can be cumbersome to use
// variables defined inside cannot be used outside, etc.
// maybe consider effect.ts in the future
/**
 * Safely executes a promise and returns a tuple containing [error, data]
 * @param promise
 * @returns [error, data]
 */

type Success<T> = [null, T];
type Failure<E> = [E, null];
type ResultSync<T, E> = Success<T> | Failure<E>;
type ResultAsync<T, E> = Promise<ResultSync<T, E>>;

// Overload for asynchronous (Promise-based) operations
/**
 * Safely executes a Promise-returning operation and returns a Promise
 * resolving to a tuple of `[error, data]`.
 *
 * @template T The type of the data returned by the successful operation.
 * @template E The type of the error object if the operation fails. Defaults to `Error`.
 * @param operation The Promise to execute.
 * @returns A Promise resolving to `[null, data]` on success, or `[error, null]` on failure.
 */
export function tryCatch<T, E = Error>(operation: Promise<T>): ResultAsync<T, E>;

// Overload for synchronous (function-based) operations
/**
 * Safely executes a synchronous function and returns a tuple of `[error, data]`.
 *
 * @template T The type of the data returned by the successful operation.
 * @template E The type of the error object if the operation fails. Defaults to `Error`.
 * @param operation The synchronous function to execute.
 * @returns A tuple `[null, data]` on success, or `[error, null]` on failure.
 */
export function tryCatch<T, E = Error>(operation: () => T): ResultSync<T, E>;

// Implementation (this signature is not directly called by users if overloads match)
export function tryCatch<T, E = Error>(
  operation: Promise<T> | (() => T),
): ResultSync<T, E> | ResultAsync<T, E> {
  if (operation !== null && typeof (operation as { then?: unknown }).then === 'function') {
    // Asynchronous path for promises or then-ables.
    // Using `operation as { then?: unknown }` for a slightly safer type assertion
    // than `as any` when checking for the `then` method.
    return Promise.resolve(operation as Promise<T>)
      .then((value: T): Success<T> => {
        return [null, value];
      })
      .catch((caughtError: unknown): Failure<E> => {
        // Changed from 'any' to 'unknown'
        // On rejection, `caughtError` is of type `unknown`.
        // This is safer than `any` as it forces an explicit assertion or type check.
        // We assert to `E` as per the function's generic error type.
        return [caughtError as E, null];
      });
  } else {
    // Synchronous path for regular functions.
    try {
      const data = (operation as () => T)();
      return [null, data as T];
    } catch (caughtError: unknown) {
      // This was already 'unknown' or is good practice
      // If an error is thrown, `caughtError` is `unknown`.
      // We assert to `E`, similar to the asynchronous path.
      return [caughtError as E, null];
    }
  }
}
